import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import '../styles/AuthPages.css';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    authType: 'email', // 'email' or 'phone'
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    age: '',
    gender: '',
    role: 'customer',
    location: '',
    services: [],
    bio: '',
    hourlyRate: '',
    coordinates: {
      lat: null,
      lng: null
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');

  const serviceOptions = [
    { value: 'sweeping-mopping', label: '🧹 Sweeping & Mopping' },
    { value: 'cooking', label: '👨‍🍳 Cooking' },
    { value: 'laundry', label: '👕 Laundry' },
    { value: 'gardening', label: '🌱 Gardening' },
    { value: 'pet-care', label: '🐕 Pet Care' },
    { value: 'elderly-care', label: '👴 Elderly Care' },
    { value: 'babysitting', label: '👶 Babysitting' },
    { value: 'hair-dresser', label: '✂️ Hair Dresser' },
    { value: 'other', label: '🔧 Other' }
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  // Try to get user's location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData(prev => ({
            ...prev,
            coordinates: {
              lat: latitude,
              lng: longitude
            }
          }));
          
          // Try to get location name using reverse geocoding
          fetchLocationNameFromCoordinates(latitude, longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Don't show error initially, only when user tries to use location button
        }
      );
    }
  }, []);

  const fetchLocationNameFromCoordinates = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data.display_name) {
        // Extract city/area from the response
        const city = data.address?.city || data.address?.town || data.address?.village || 
                    data.address?.state_district || data.address?.state || '';
        
        if (city) {
          setFormData(prev => ({
            ...prev,
            location: city
          }));
        }
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    }
  };

  const fetchCoordinatesFromLocation = async (locationName) => {
    if (!locationName.trim()) return;
    
    setLocationLoading(true);
    setLocationError('');
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationName)}`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        setFormData(prev => ({
          ...prev,
          coordinates: {
            lat: parseFloat(lat),
            lng: parseFloat(lon)
          }
        }));
      } else {
        setLocationError('Could not find coordinates for this location');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setLocationError('Error finding location coordinates');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If location is changed, try to fetch coordinates
    if (name === 'location' && value.trim().length > 3) {
      // Add debounce to avoid too many requests
      const timeoutId = setTimeout(() => {
        fetchCoordinatesFromLocation(value);
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  };

  const handleAuthTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      authType: type,
      // Clear the other field when switching
      email: type === 'email' ? prev.email : '',
      phone: type === 'phone' ? prev.phone : ''
    }));
  };

  const handleServiceChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      services: checked 
        ? [...prev.services, value]
        : prev.services.filter(service => service !== value)
    }));
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setLocationLoading(true);
      setLocationError('');
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData(prev => ({
            ...prev,
            coordinates: {
              lat: latitude,
              lng: longitude
            }
          }));
          
          // Get location name using reverse geocoding
          fetchLocationNameFromCoordinates(latitude, longitude);
          setLocationLoading(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationError('Unable to get your location. Please enable location services.');
          setLocationLoading(false);
        }
      );
    } else {
      setLocationError('Geolocation is not supported by this browser.');
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }

    if (formData.authType === 'email') {
      if (!formData.email.trim()) {
        setError('Email is required');
        return false;
      }
      if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
        setError('Please provide a valid email address');
        return false;
      }
    } else {
      if (!formData.phone.trim()) {
        setError('Phone number is required');
        return false;
      }
      if (!/^[6-9]\d{9}$/.test(formData.phone)) {
        setError('Please provide a valid 10-digit mobile number');
        return false;
      }
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!formData.age || formData.age < 18 || formData.age > 100) {
      setError('Age must be between 18 and 100');
      return false;
    }

    if (!formData.gender) {
      setError('Please select a gender');
      return false;
    }

    if (!formData.location.trim()) {
      setError('Location is required');
      return false;
    }

    if (formData.role === 'helper') {
      if (formData.services.length === 0) {
        setError('Please select at least one service');
        return false;
      }
      if (!formData.hourlyRate || formData.hourlyRate <= 0) {
        setError('Please provide a valid hourly rate');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const userData = {
        name: formData.name,
        password: formData.password,
        age: parseInt(formData.age),
        gender: formData.gender,
        role: formData.role,
        location: formData.location
      };

      // Add email or phone based on selected auth type
      if (formData.authType === 'email') {
        userData.email = formData.email;
      } else {
        userData.phone = formData.phone;
      }

      // Add coordinates if available
      if (formData.coordinates.lat && formData.coordinates.lng) {
        userData.coordinates = {
          type: 'Point',
          coordinates: [formData.coordinates.lng, formData.coordinates.lat] // GeoJSON format: [longitude, latitude]
        };
      }

      // Add helper-specific data
      if (formData.role === 'helper') {
        userData.services = formData.services;
        userData.hourlyRate = parseInt(formData.hourlyRate);
        if (formData.bio.trim()) {
          userData.bio = formData.bio;
        }
      }

      await register(userData);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>👋 Join Sahayak</h1>
            <p>Create your account to get started</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            {/* Name */}
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
              />
            </div>

            {/* Auth Type Selection */}
            <div className="form-group">
              <label>Choose Login Method</label>
              <div className="auth-type-selector">
                <button
                  type="button"
                  className={`auth-type-btn ${formData.authType === 'email' ? 'active' : ''}`}
                  onClick={() => handleAuthTypeChange('email')}
                >
                  📧 Email Address
                </button>
                <button
                  type="button"
                  className={`auth-type-btn ${formData.authType === 'phone' ? 'active' : ''}`}
                  onClick={() => handleAuthTypeChange('phone')}
                >
                  📱 Phone Number
                </button>
              </div>
            </div>

            {/* Email or Phone based on selection */}
            {formData.authType === 'email' ? (
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  required
                />
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="phone">Mobile Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  required
                />
                <small className="form-hint">Enter without +91 or country code</small>
              </div>
            )}

            {/* Age */}
            <div className="form-group">
              <label htmlFor="age">Age</label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                placeholder="Enter your age"
                min="18"
                max="100"
                required
              />
            </div>

            {/* Gender */}
            <div className="form-group">
              <label htmlFor="gender">Gender</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                required
              >
                <option value="" disabled>Select your gender</option>
                {genderOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Create a strong password"
                required
              />
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                required
              />
            </div>

            {/* Role Selection */}
            <div className="form-group">
              <label>I want to</label>
              <div className="role-selector">
                <label className={`role-option ${formData.role === 'customer' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="customer"
                    checked={formData.role === 'customer'}
                    onChange={handleInputChange}
                  />
                  <div className="role-content">
                    <div className="role-icon">🏠</div>
                    <div className="role-text">
                      <h3>Find Helpers</h3>
                      <p>Book services from helpers</p>
                    </div>
                  </div>
                </label>

                <label className={`role-option ${formData.role === 'helper' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="helper"
                    checked={formData.role === 'helper'}
                    onChange={handleInputChange}
                  />
                  <div className="role-content">
                    <div className="role-icon">🛠️</div>
                    <div className="role-text">
                      <h3>Offer Services</h3>
                      <p>Provide services to customers</p>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Location */}
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <div className="location-input-group">
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Enter your city/area"
                  required
                />
                <button 
                  type="button"
                  className="location-btn-small"
                  onClick={getCurrentLocation}
                  disabled={locationLoading}
                  title="Use my current location"
                  style={{ 
                    background: '#4F46E5', 
                    color: 'white', 
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)'
                  }}
                >
                  {locationLoading ? '⏳' : '📍'}
                </button>
              </div>
              {locationError && <small className="form-error">{locationError}</small>}
              {formData.coordinates.lat && formData.coordinates.lng && (
                <small className="form-success">
                  ✅ Location coordinates captured
                </small>
              )}
            </div>

            {/* Helper-specific fields */}
            {formData.role === 'helper' && (
              <>
                <div className="form-group">
                  <label>Services Offered</label>
                  <div className="services-grid">
                    {serviceOptions.map(service => (
                      <label key={service.value} className="service-checkbox">
                        <input
                          type="checkbox"
                          value={service.value}
                          checked={formData.services.includes(service.value)}
                          onChange={handleServiceChange}
                        />
                        <span className="checkmark"></span>
                        {service.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="hourlyRate">Hourly Rate (₹)</label>
                  <input
                    type="number"
                    id="hourlyRate"
                    name="hourlyRate"
                    value={formData.hourlyRate}
                    onChange={handleInputChange}
                    placeholder="Enter your hourly rate"
                    min="50"
                    max="2000"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bio">Bio (Optional)</label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Tell customers about your experience and skills"
                    rows="3"
                    maxLength="500"
                  />
                </div>
              </>
            )}

            <button 
              type="submit" 
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Already have an account? 
              <Link to="/login" className="auth-link">Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage; 